import {
  Button,
  Typography,
  TextField,
  InputAdornment,
  Box,
  useTheme,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import SearchIcon from '@material-ui/icons/Search';
import WidgetsIcon from '@material-ui/icons/Widgets';
import StorefrontIcon from '@material-ui/icons/Storefront';
import LockIcon from '@material-ui/icons/Lock';
import CloudIcon from '@material-ui/icons/Cloud';
import SaveIcon from '@material-ui/icons/Save';
import FlashOnIcon from '@material-ui/icons/FlashOn';
import DescriptionIcon from '@material-ui/icons/Description';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import GroupWorkIcon from '@material-ui/icons/GroupWork';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { okdConsoleUrl } from '../constants/okdClusters';

const PulseKeyframes = () =>
  createPortal(
    <style>{`
      @keyframes bp-pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.4; }
      }

      @keyframes bp-shimmer {
        0% { background-position: -200% 0; }
        100% { background-position: 200% 0; }
      }

      @keyframes bp-fadeIn {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
    `}</style>,
    document.head,
  );

type SectionMenuProps = {
  section: 'Documentation' | 'Marketplace' | 'Hydra Tools' | 'OKD Consoles';
};

type SectionMenuItem = {
  label: string;
  icon: JSX.Element;
  to: string;
  color: string;
};

export const SectionMenu = ({ section }: SectionMenuProps) => {
  const navigate = useNavigate();
  const configApi = useApi(configApiRef);
  const theme = useTheme();
  const isDark = theme.palette.type === 'dark';

  const teamsAnnouncements =
    configApi.getOptionalString('teams.channels.announcements') ?? '';
  const teamsGeneral =
    configApi.getOptionalString('teams.channels.general') ?? '';
  const teamsGravitee =
    configApi.getOptionalString('teams.channels.graviteeApim') ?? '';

  const sectionMenus: Record<SectionMenuProps['section'], SectionMenuItem[]> = {
    Documentation: [
      {
        label: 'Hydra Docs',
        icon: <DescriptionIcon />,
        to: '/docs/default/component/hydra-docs',
        color: '#10b981',
      },
      ...(teamsAnnouncements
        ? [
            {
              label: 'Hydra Announcements',
              icon: <NotificationsActiveIcon />,
              to: teamsAnnouncements,
              color: '#ec4899',
            },
          ]
        : []),
      ...(teamsGeneral
        ? [
            {
              label: 'Hydra General',
              icon: <GroupWorkIcon />,
              to: teamsGeneral,
              color: '#6366f1',
            },
          ]
        : []),
      ...(teamsGravitee
        ? [
            {
              label: 'Gravitee APIM',
              icon: <WidgetsIcon />,
              to: teamsGravitee,
              color: '#8b5cf6',
            },
          ]
        : []),
      {
        label: 'API Guidelines',
        icon: <HelpIcon />,
        to: '/docs/default/component/api-guidelines',
        color: '#0ea5e9',
      },
      {
        label: 'GES Onboarding',
        icon: <DescriptionIcon />,
        to: '/docs/default/component/ges-onboarding',
        color: '#14b8a6',
      },
      {
        label: 'Docs MCP',
        icon: <DescriptionIcon />,
        to: '/docs/default/component/mcp-server',
        color: '#f59e0b',
      },
      {
        label: 'Metering',
        icon: <DescriptionIcon />,
        to: '/docs/default/component/hydra-docs/platform-core-services/metering/what-is/',
        color: '#007FFF',
      },
      {
        label: 'Identity',
        icon: <DescriptionIcon />,
        to: '/docs/default/component/hydra-docs/platform-core-services/identity/what-is/',
        color: '#6366f1',
      },
      {
        label: 'Operators',
        icon: <WidgetsIcon />,
        to: '/operators',
        color: '#8b5cf6',
      },
    ],

    Marketplace: [
      {
        label: 'AI Marketplace',
        icon: <StorefrontIcon />,
        to: '/ai-marketplace',
        color: '#007FFF',
      },
    ],

    'Hydra Tools': [
      {
        label: 'Vault',
        icon: <LockIcon />,
        to: 'https://vault.hydra.verint.com/',
        color: '#f59e0b',
      },
      {
        label: 'Quay',
        icon: <SaveIcon />,
        to: 'https://quay.hydra.verint.com/',
        color: '#ee0000',
      },
      {
        label: 'Harness',
        icon: <FlashOnIcon />,
        to: 'https://app.harness.io/ng/account/20ZrFxKoQ8ieGARA-Z8pVg/all/orgs',
        color: '#00ade4',
      },
      {
        label: 'API Management',
        icon: <WidgetsIcon />,
        to: 'https://console.api.verint.com/',
        color: '#8b5cf6',
      },
      {
        label: 'API Dev Portal',
        icon: <WidgetsIcon />,
        to: 'https://developer.api.verint.com',
        color: '#6366f1',
      },
    ],

    'OKD Consoles': [
      {
        label: 'US-EAST-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('us-east-1'),
        color: '#ef4444',
      },
      {
        label: 'EU-CENTRAL-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('eu-central-1'),
        color: '#f59e0b',
      },
      {
        label: 'US-EAST-2',
        icon: <CloudIcon />,
        to: okdConsoleUrl('us-east-2'),
        color: '#0ea5e9',
      },
      {
        label: 'US-WEST-2',
        icon: <CloudIcon />,
        to: okdConsoleUrl('us-west-2'),
        color: '#14b8a6',
      },
      {
        label: 'CA-CENTRAL-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('ca-central-1'),
        color: '#6366f1',
      },
      {
        label: 'EU-WEST-2',
        icon: <CloudIcon />,
        to: okdConsoleUrl('eu-west-2'),
        color: '#8b5cf6',
      },
      {
        label: 'AP-SOUTHEAST-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('ap-southeast-1'),
        color: '#ec4899',
      },
      {
        label: 'AP-SOUTHEAST-2',
        icon: <CloudIcon />,
        to: okdConsoleUrl('ap-southeast-2'),
        color: '#10b981',
      },
      {
        label: 'AF-SOUTH-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('af-south-1'),
        color: '#007FFF',
      },
      {
        label: 'EAST US (Azure)',
        icon: <CloudIcon />,
        to: okdConsoleUrl('eus-azure'),
        color: '#00ade4',
      },
      {
        label: `GERMANY WEST\nCENTRAL (Azure)`,
        icon: <CloudIcon />,
        to: okdConsoleUrl('gwc-azure'),
        color: '#632ca6',
      },
      {
        label: `BETA\nUS-EAST-2`,
        icon: <CloudIcon />,
        to: okdConsoleUrl('beta-us-east-2'),
        color: '#f97316',
      },
      {
        label: 'MGMT-1',
        icon: <CloudIcon />,
        to: okdConsoleUrl('mgmt-1'),
        color: '#64748b',
      },
    ],
  };

  const allItems = sectionMenus[section] || [];
  const [search, setSearch] = useState('');

  const items = search.trim()
    ? allItems.filter(item =>
        item.label.toLowerCase().includes(search.toLowerCase()),
      )
    : allItems;

  const isCompactSection =
    section === 'OKD Consoles' || section === 'Hydra Tools';

  return (
    <Box
      style={{
        width: '100%',
        maxWidth: isCompactSection ? 1200 : 1000,
        marginLeft: 'auto',
        marginRight: 'auto',
        padding: '40px 32px 0',
      }}
    >
      <PulseKeyframes />

      {isCompactSection && (
        <TextField
          variant="outlined"
          size="small"
          placeholder={
            section === 'OKD Consoles' ? 'Filter by region...' : 'Filter tools...'
          }
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            display: 'block',
            margin: '0 auto 32px',
            width: 320,
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon style={{ fontSize: 18, opacity: 0.5 }} />
              </InputAdornment>
            ),
          }}
        />
      )}

      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 14,
          paddingBottom: 24,
          overflow: 'visible',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {items.map((item, idx) => (
          <Button
            key={item.label}
            variant="contained"
            onClick={() => {
              if (item.to.startsWith('http')) {
                window.open(item.to, '_blank', 'noopener,noreferrer');
              } else if (item.to.startsWith('msteams://')) {
                window.location.href = item.to;
              } else {
                navigate(item.to);
              }
            }}
            style={{
              borderRadius: 16,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: isCompactSection ? 130 : 125,
              height: '100%',
              width: '100%',
              boxShadow: 'none',
              background: isDark
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)',
              border: `2px solid ${
                isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
              }`,
              color: isDark ? 'rgba(255,255,255,0.85)' : '#1a1a2e',
              textTransform: 'none',
              padding: '24px 16px',
              transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              animation: `bp-fadeIn 0.4s ease ${idx * 0.04}s both`,
            }}
            onMouseEnter={e => {
              e.currentTarget.style.background = `${item.color}20`;
              e.currentTarget.style.borderColor = `${item.color}60`;
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = `0 0 20px 6px ${item.color}20`;
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = isDark
                ? 'rgba(255,255,255,0.04)'
                : 'rgba(0,0,0,0.02)';
              e.currentTarget.style.borderColor = isDark
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.08)';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <Box
              style={{
                color: item.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: 5,
              }}
            >
              {item.icon}
            </Box>

            <Typography
              style={{
                fontWeight: 600,
                fontSize: 16,
                textAlign: 'center',
                lineHeight: 1.3,
                whiteSpace: 'pre-line',
                color: isDark ? 'rgba(255,255,255,0.85)' : '#1a1a2e',
              }}
            >
              {item.label}
            </Typography>
          </Button>
        ))}
      </Box>
    </Box>
  );
};